import type { ReactNode } from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  MONOLITH_BOARD_MODAL_EYEBROW_CLASS,
  MONOLITH_BOARD_MODAL_FIELD_LABEL_CLASS,
  MONOLITH_BOARD_MODAL_INPUT_CLASS,
  MONOLITH_BOARD_MODAL_TITLE_CLASS,
  MonolithBoardModal,
  MonolithBoardModalSteps,
  type MonolithBoardModalStep,
} from "@/components/dashboard/MonolithBoardModal";
import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/classNames";
import { signInWithPassword, signUpWithPassword } from "@/lib/magicLinkAuth";
import { updateCurrentProfile } from "@/lib/profileClient";
import { getSupabaseBrowserClient } from "@/lib/supabase";

type FlowView = "choice" | "sign-in" | "sign-up";

export type JoinPageAuthModalMode = "invite" | "view";

interface JoinPageAuthModalProps {
  campaignName: string;
  onAuthenticated: () => void;
  onCancel: () => void;
  mode?: JoinPageAuthModalMode;
}

interface ModeCopy {
  // Choice screen is only used by `invite` mode. `view` mode skips the choice
  // screen entirely (account creation only happens via the home page or an
  // invite link, not from a deep-link to an existing campaign), so visitors
  // land directly on the sign-in form.
  showChoiceScreen: boolean;
  eyebrow: string;
  choiceTitle?: string;
  choiceDescription?: (campaignName: string) => ReactNode;
  signInDescription: (campaignName: string) => ReactNode;
  cancelLabel: string;
}

const MODE_COPY: Record<JoinPageAuthModalMode, ModeCopy> = {
  invite: {
    showChoiceScreen: true,
    eyebrow: "Campaign Invite",
    choiceTitle: "Sign in or create an account",
    choiceDescription: (campaignName) => (
      <>You've been invited to join <strong>{campaignName}</strong>. Sign in or create an account to continue.</>
    ),
    signInDescription: (campaignName) => (
      <>Enter your email and password to join <strong>{campaignName}</strong>.</>
    ),
    cancelLabel: "Cancel",
  },
  view: {
    showChoiceScreen: false,
    eyebrow: "Campaign",
    signInDescription: (campaignName) => (
      <>Enter your email and password to view <strong>{campaignName}</strong>.</>
    ),
    cancelLabel: "Go home",
  },
};

const SIGN_UP_STEPS = [
  {
    key: "account" as const,
    title: "Create your account",
    description: "Sign up with an email and password.",
    label: "",
    placeholder: "",
  },
  {
    key: "displayName" as const,
    title: "Choose your username",
    description: "This will identify you in the campaign.",
    label: "Username",
    placeholder: "hexmancer42",
  },
];

type StepKey = (typeof SIGN_UP_STEPS)[number]["key"];

const STEP_LABELS: Record<StepKey, string> = {
  account: "Account",
  displayName: "Username",
};

export default function JoinPageAuthModal({
  campaignName,
  onAuthenticated,
  onCancel,
  mode = "invite",
}: JoinPageAuthModalProps) {
  const copy = MODE_COPY[mode];
  const [view, setView] = useState<FlowView>(copy.showChoiceScreen ? "choice" : "sign-in");
  const [stepIndex, setStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");

  const [signInEmail, setSignInEmail] = useState("");
  const [signInPassword, setSignInPassword] = useState("");

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const currentStep = SIGN_UP_STEPS[stepIndex];
  const isAccountStep = currentStep?.key === "account";
  const canContinueSignUp = isAccountStep
    ? email.trim().length > 0 && password.length >= 6
    : displayName.trim().length > 0;
  const canSubmitSignIn =
    signInEmail.trim().length > 0 && signInPassword.length > 0 && !submitting;

  const modalSteps: MonolithBoardModalStep[] = SIGN_UP_STEPS.map((step, index) => ({
    id: step.key,
    label: STEP_LABELS[step.key],
    status: index === stepIndex ? "current" : index < stepIndex ? "complete" : "upcoming",
    onSelect: () => setStepIndex(index),
  }));

  const handleSignIn = useCallback(async () => {
    if (!canSubmitSignIn) return;
    setSubmitting(true);
    setError(null);
    try {
      const supabase = getSupabaseBrowserClient();
      await signInWithPassword({ supabase, email: signInEmail.trim(), password: signInPassword });
      if (mountedRef.current) onAuthenticated();
    } catch (err) {
      if (mountedRef.current) setError(err instanceof Error ? err.message : "Sign-in failed");
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  }, [canSubmitSignIn, signInEmail, signInPassword, onAuthenticated]);

  const handleSignUpStep = useCallback(async () => {
    if (!currentStep || !canContinueSignUp || submitting) return;

    if (stepIndex < SIGN_UP_STEPS.length - 1) {
      setStepIndex((i) => i + 1);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      const supabase = getSupabaseBrowserClient();
      await signUpWithPassword({ supabase, email: email.trim(), password });
      await updateCurrentProfile({ displayName: displayName.trim() });
      if (mountedRef.current) onAuthenticated();
    } catch (err) {
      if (mountedRef.current) setError(err instanceof Error ? err.message : "Sign-up failed");
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  }, [currentStep, canContinueSignUp, submitting, stepIndex, email, password, displayName, onAuthenticated]);

  return (
    <MonolithBoardModal
      open
      titleId="join-auth-title"
      onClose={onCancel}
      submitting={submitting}
    >
      {error ? (
        <div className="mb-4 border-[3px] border-[#7a1d16] bg-[#fff1ea] px-4 py-3 text-sm text-[#7a1d16]">
          {error}
        </div>
      ) : null}

      {view === "choice" ? (
        <div className="flex flex-col">
          <p className={MONOLITH_BOARD_MODAL_EYEBROW_CLASS}>{copy.eyebrow}</p>
          <h2 id="join-auth-title" className={cn("mt-2", MONOLITH_BOARD_MODAL_TITLE_CLASS)}>
            {copy.choiceTitle}
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-neutral-800">
            {copy.choiceDescription?.(campaignName)}
          </p>
          <div className="mt-8 flex flex-col gap-3 border-t-2 border-neutral-950/12 pt-6">
            <Button onClick={() => { setError(null); setView("sign-in"); }} variant="primary" chrome="frame" type="button">
              Sign In
            </Button>
            <Button onClick={() => { setError(null); setView("sign-up"); }} variant="secondary" chrome="frame" type="button">
              Create Account
            </Button>
            <Button onClick={onCancel} variant="cancel" chrome="frame" type="button">
              {copy.cancelLabel}
            </Button>
          </div>
        </div>
      ) : view === "sign-in" ? (
        <div className="flex flex-col">
          <p className={MONOLITH_BOARD_MODAL_EYEBROW_CLASS}>Welcome back</p>
          <h2 id="join-auth-title" className={cn("mt-2", MONOLITH_BOARD_MODAL_TITLE_CLASS)}>
            Sign in
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-neutral-800">
            {copy.signInDescription(campaignName)}
          </p>
          <div className="mt-6 space-y-4">
            <label className="block">
              <span className={MONOLITH_BOARD_MODAL_FIELD_LABEL_CLASS}>Email address</span>
              <input
                type="email"
                value={signInEmail}
                autoFocus
                required
                autoComplete="email"
                onChange={(e) => setSignInEmail(e.target.value)}
                className={MONOLITH_BOARD_MODAL_INPUT_CLASS}
                placeholder="you@example.com"
              />
            </label>
            <label className="block">
              <span className={MONOLITH_BOARD_MODAL_FIELD_LABEL_CLASS}>Password</span>
              <input
                type="password"
                value={signInPassword}
                required
                autoComplete="current-password"
                onChange={(e) => setSignInPassword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { e.preventDefault(); void handleSignIn(); }
                }}
                className={MONOLITH_BOARD_MODAL_INPUT_CLASS}
                placeholder="Your password"
              />
            </label>
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-3 border-t-2 border-neutral-950/12 pt-6">
            <Button
              onClick={() => {
                setError(null);
                if (copy.showChoiceScreen) {
                  setView("choice");
                } else {
                  onCancel();
                }
              }}
              disabled={submitting}
              variant="cancel"
              chrome="frame"
              type="button"
            >
              {copy.showChoiceScreen ? "Back" : copy.cancelLabel}
            </Button>
            <Button onClick={() => void handleSignIn()} disabled={!canSubmitSignIn} variant="primary" chrome="frame" type="button">
              {submitting ? "Signing in\u2026" : "Sign In"}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <MonolithBoardModalSteps
            steps={modalSteps}
            stepNumber={stepIndex + 1}
            stepCount={SIGN_UP_STEPS.length}
            currentStepLabel={currentStep ? STEP_LABELS[currentStep.key] : ""}
            disabled={submitting}
          />

          {currentStep ? (
            <div className="mt-6">
              <h2
                id="join-auth-title"
                className="text-lg font-black uppercase tracking-[-0.04em] text-neutral-950 sm:text-xl"
              >
                {currentStep.title}
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-neutral-800">{currentStep.description}</p>

              {isAccountStep ? (
                <div className="mt-6 space-y-4">
                  <label className="block">
                    <span className={MONOLITH_BOARD_MODAL_FIELD_LABEL_CLASS}>Email address</span>
                    <input
                      type="email"
                      value={email}
                      autoFocus
                      required
                      autoComplete="email"
                      onChange={(e) => setEmail(e.target.value)}
                      className={MONOLITH_BOARD_MODAL_INPUT_CLASS}
                      placeholder="you@example.com"
                    />
                  </label>
                  <label className="block">
                    <span className={MONOLITH_BOARD_MODAL_FIELD_LABEL_CLASS}>Password</span>
                    <input
                      type="password"
                      value={password}
                      required
                      minLength={6}
                      autoComplete="new-password"
                      onChange={(e) => setPassword(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") { e.preventDefault(); void handleSignUpStep(); }
                      }}
                      className={MONOLITH_BOARD_MODAL_INPUT_CLASS}
                      placeholder="At least 6 characters"
                    />
                  </label>
                </div>
              ) : (
                <label className="mt-6 block">
                  <span className={MONOLITH_BOARD_MODAL_FIELD_LABEL_CLASS}>{currentStep.label}</span>
                  <input
                    type="text"
                    value={displayName}
                    autoFocus
                    required
                    autoComplete="off"
                    onChange={(e) => setDisplayName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") { e.preventDefault(); void handleSignUpStep(); }
                    }}
                    className={MONOLITH_BOARD_MODAL_INPUT_CLASS}
                    placeholder={currentStep.placeholder}
                  />
                </label>
              )}
            </div>
          ) : null}

          <div className="mt-6 flex flex-wrap items-center gap-3 border-t-2 border-neutral-950/12 pt-6">
            {stepIndex > 0 ? (
              <Button onClick={() => setStepIndex((i) => Math.max(0, i - 1))} disabled={submitting} variant="cancel" chrome="frame" type="button">
                Back
              </Button>
            ) : (
              <Button onClick={() => { setError(null); setView("choice"); }} disabled={submitting} variant="cancel" chrome="frame" type="button">
                Back
              </Button>
            )}
            <Button
              onClick={() => void handleSignUpStep()}
              disabled={!canContinueSignUp || submitting}
              variant="primary"
              chrome="frame"
              type="button"
            >
              {submitting
                ? "Creating account\u2026"
                : stepIndex === SIGN_UP_STEPS.length - 1
                  ? "Create account"
                  : "Continue"}
            </Button>
          </div>
        </>
      )}
    </MonolithBoardModal>
  );
}
